import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Search, Info, Video } from "lucide-react";
import { registrarRecursoReciente } from "../utils/recientes";
import { emitFavoritosActualizados } from "../utils/favoritos";
import DOMPurify from "dompurify";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const HEADER_BASE_HEIGHT = 75;
const MODO_DESARROLLO = process.env.REACT_APP_MODO_DESARROLLO === "true";
const DEV_BANNER_HEIGHT = 40;
const FALLBACK_RESOURCE_IMAGE = "/placeholder.png";
const IMAGE_RETRY_DELAY_MS = 300;

function isDataImageUrl(value) {
  return typeof value === "string" && value.startsWith("data:image/");
}

async function fetchResourceImageWithRetry(recursoId) {
  const fetchOnce = async () => {
    const imgRes = await fetch(`${API_BASE}/recursos/${recursoId}/imagen`);
    if (!imgRes.ok) throw new Error("image_fetch_failed");
    const txt = await imgRes.text();
    if (!isDataImageUrl(txt)) throw new Error("invalid_image_payload");
    return txt;
  };

  try {
    return await fetchOnce();
  } catch {
    try {
      await new Promise((resolve) => setTimeout(resolve, IMAGE_RETRY_DELAY_MS));
      return await fetchOnce();
    } catch {
      return FALLBACK_RESOURCE_IMAGE;
    }
  }
}

function getAuthValue(key) {
  const ls = localStorage.getItem(key);
  if (ls !== null && ls !== "undefined") return ls;
  const ss = sessionStorage.getItem(key);
  return ss !== "undefined" ? ss : null;
}

function buildRecursoGoUrl(apiBase, recursoId) {
  const base = String(apiBase || "/api").replace(/\/$/, "");
  const href = `${base}/recursos/${recursoId}/go`;
  const token = getAuthValue("token");
  if (!token) return href;
  return `${href}?token=${encodeURIComponent(token)}`;
}

function hasTutorialUrl(recurso) {
  return !!String(recurso?.urlTutorial || "").trim();
}

function TutorialLink({ recurso, className = "" }) {
  if (!hasTutorialUrl(recurso)) return null;

  return (
    <a
      href={String(recurso.urlTutorial).trim()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={className}
      title="Ver tutorial"
      aria-label={`Ver tutorial de ${recurso.nombre}`}
    >
      <Video className="w-4 h-4" />
      <span>Tutorial</span>
    </a>
  );
}

function buildAnonUser() {
  return {
    codigoErp: "99999999999",
    tipoIdentificacion: "CEDULA",
    identificacion: "99999999999",
    nombres: "ANONIMO",
    apellidos: "(Acceso público)",
    numeroCelular: "",
    emailInstitucional: "anonimo@ucacue.edu.ec",
    emailPersonal: "",
    sexo: "N/A",
    rolesUsuario: [
      {
        tipoRol: "ANONIMO",
        detallesRol: [
          {
            nombreCarrera: "Acceso Público",
            unidadAcademica: "Visitante",
            sede: "Externo",
            modalidad: "Web",
            curso: "",
            paralelo: "",
            nombreRol: "ANONIMO",
            unidadOrganizativa: "Visitante",
          },
        ],
      },
    ],
  };
}

function logUsoRecurso(apiBase, recursoId) {
  const url = `${apiBase}/log-uso/registrar/${recursoId}`;
  const esAnonimo = getAuthValue("anonimo") === "true";

  let baseUser;
  if (esAnonimo) {
    baseUser = buildAnonUser();
  } else {
    baseUser = buildAnonUser();
  }

  if (navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(baseUser)], { type: "application/json" });
    navigator.sendBeacon(url, blob);
  }
}

function InfoModal({ recurso, imagenSrc, onClose }) {
  const [imgOk, setImgOk] = React.useState(true);
  const [imgLoaded, setImgLoaded] = React.useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!recurso) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white/95 border border-gray-200 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3
            id="modal-title"
            className="text-base md:text-lg font-semibold truncate"
          >
            {recurso.nombre}
          </h3>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border hover:bg-gray-100"
            aria-label="Cerrar"
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="bg-gray-50">
          {!imgLoaded && (
            <div className="w-full max-h-[60vh] h-[220px] animate-pulse bg-gray-200" />
          )}
          {imgOk ? (
            <img
              src={imagenSrc || FALLBACK_RESOURCE_IMAGE}
              alt={recurso.nombre}
              className={`w-full object-contain mx-auto ${imgLoaded ? "block" : "hidden"}`}
              style={{ maxHeight: "60vh" }}
              onLoad={() => setImgLoaded(true)}
              onError={() => {
                setImgOk(false);
                setImgLoaded(true);
              }}
            />
          ) : (
            <div className="w-full max-h-[60vh] h-[220px] flex items-center justify-center text-gray-500">
              Sin imagen disponible
            </div>
          )}
        </div>
        <div className="px-5 pt-4 pb-5">
          <div className="flex flex-wrap gap-2 mb-3">
            {recurso?.categoria?.nombre && (
              <span className="px-2.5 py-1 text-xs rounded-full bg-rose-100 text-rose-700 border border-rose-200">
                {recurso.categoria.nombre}
              </span>
            )}
            {Array.isArray(recurso?.areas) &&
              recurso.areas.map((a) => (
                <span
                  key={a.id || a.nombre}
                  className="px-2.5 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200"
                >
                  {a.nombre}
                </span>
              ))}
          </div>

          <TutorialLink
            recurso={recurso}
            className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 text-sm font-semibold"
          />

          <div
            className="prose prose-sm max-w-none text-gray-800 leading-relaxed
                      [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mt-2 [&_h4]:mb-1
                      [&_h5]:text-sm  [&_h5]:font-semibold [&_h5]:mt-2 [&_h5]:mb-1
                      [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5
                      [&_blockquote]:border-l-4 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600
                      max-h-[28vh] overflow-auto pr-1"
            style={{ scrollbarWidth: "thin" }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(recurso.descripcion || "", {
                ADD_ATTR: ["target", "rel"],
              }),
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function CategoriaPage() {
  const { nombreCategoria } = useParams();
  const navigate = useNavigate();

  const [recursos, setRecursos] = useState([]);
  const [imagenes, setImagenes] = useState({});
  const [areas, setAreas] = useState([]);
  const [filtroAreas, setFiltroAreas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(true);
  const [favoritos, setFavoritos] = useState([]);
  const [recursoSeleccionado, setRecursoSeleccionado] = useState(null);

  const ITEMS_POR_PAGINA = 12;

  const offsetTop =
    HEADER_BASE_HEIGHT + (MODO_DESARROLLO ? DEV_BANNER_HEIGHT : 0);

  // Decodificar el nombre de la categoría
  const nombreDecodificado = decodeURIComponent(nombreCategoria || "");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const esInvitado = getAuthValue("guest") === "true";
        const esAnonimo = getAuthValue("anonimo") === "true";
        const modoLimitado = esInvitado || esAnonimo;

        const recursosUrl = modoLimitado
          ? `${API_BASE}/recursos?anon=true`
          : `${API_BASE}/recursos`;

        const recRes = await fetch(recursosUrl);
        const recursosData = await recRes.json();

        // Filtrar por categoría y estado activo
        const recursosFiltrados = recursosData.filter(
          (r) =>
            r.estado !== "INACTIVO" && r.categoria?.nombre === nombreDecodificado
        );

        // Obtener todas las áreas únicas
        const todasLasAreas = recursosFiltrados.flatMap((r) => r.areas || []);
        const nombresUnicos = Array.from(
          new Set(todasLasAreas.map((a) => a.nombre))
        );
        setAreas(nombresUnicos);

        // Cargar imágenes
        const paresImagenes = await Promise.all(
          recursosFiltrados.map(async (recurso) => [
            recurso.id,
            await fetchResourceImageWithRetry(recurso.id),
          ])
        );
        const imagenesTemp = Object.fromEntries(paresImagenes);

        setRecursos(recursosFiltrados);
        setImagenes(imagenesTemp);
        setPagina(1);
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [nombreDecodificado]);

  useEffect(() => {
    const esInvitado = getAuthValue("guest") === "true";
    const esAnonimo = getAuthValue("anonimo") === "true";

    if (esInvitado || esAnonimo) {
      localStorage.removeItem("favoritos");
    }

    const esAutenticado =
      !esInvitado && !esAnonimo && !!getAuthValue("token");
    const storage = esAutenticado ? localStorage : sessionStorage;
    const favs = JSON.parse(storage.getItem("favoritos") || "[]");
    setFavoritos(favs);

    if (!esAutenticado) return;

    const token = getAuthValue("token");
    const syncFavoritosDesdeDb = async () => {
      try {
        await fetch(`${API_BASE}/mi-biblioteca/favoritos/merge`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ recursoIds: favs.map((r) => r.id) }),
        });

        const resp = await fetch(`${API_BASE}/mi-biblioteca/favoritos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;

        const favoritosDb = await resp.json();
        localStorage.setItem("favoritos", JSON.stringify(favoritosDb));
        setFavoritos(favoritosDb);
        emitFavoritosActualizados();
      } catch (err) {
        console.warn("No se pudo sincronizar favoritos en categoria:", err);
      }
    };

    syncFavoritosDesdeDb();
  }, []);

  // Filtrar recursos por búsqueda y área
  const recursosFiltrados = recursos.filter((r) => {
    const coincidenBusqueda =
      !busqueda ||
      r.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      (r.descripcion &&
        r.descripcion.toLowerCase().includes(busqueda.toLowerCase()));

    const coincidenArea =
      filtroAreas.length === 0 ||
      r.areas?.some((a) => filtroAreas.includes(a.nombre));

    return coincidenBusqueda && coincidenArea;
  });

  // Páginación
  const totalPaginas = Math.ceil(recursosFiltrados.length / ITEMS_POR_PAGINA);
  const inicio = (pagina - 1) * ITEMS_POR_PAGINA;
  const recursosPaginados = recursosFiltrados.slice(
    inicio,
    inicio + ITEMS_POR_PAGINA
  );

  const toggleArea = (area) => {
    setFiltroAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
    setPagina(1);
  };

  const limpiarFiltros = () => {
    setFiltroAreas([]);
    setBusqueda("");
    setPagina(1);
  };

  const toggleFavorito = (recurso) => {
    const esInvitado = getAuthValue("guest") === "true";
    const esAnonimo = getAuthValue("anonimo") === "true";
    const esAutenticado =
      !esInvitado && !esAnonimo && !!getAuthValue("token");
    const storage = esAutenticado ? localStorage : sessionStorage;

    let favoritosActuales = JSON.parse(storage.getItem("favoritos")) || [];
    const existe = favoritosActuales.some((r) => r.id === recurso.id);

    if (existe) {
      favoritosActuales = favoritosActuales.filter((r) => r.id !== recurso.id);
    } else {
      favoritosActuales.push(recurso);
    }

    storage.setItem("favoritos", JSON.stringify(favoritosActuales));
    setFavoritos(favoritosActuales);
    emitFavoritosActualizados();

    if (esAutenticado) {
      const token = getAuthValue("token");
      const method = existe ? "DELETE" : "POST";
      fetch(`${API_BASE}/mi-biblioteca/favoritos/${recurso.id}`, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      }).catch((err) =>
        console.warn("No se pudo sincronizar favorito en backend:", err)
      );
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ paddingTop: offsetTop }}
    >
      {/* Header con título y filtros */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40" style={{ top: offsetTop }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Botón atrás y título */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100"
              title="Atrás"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {nombreDecodificado}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {recursosFiltrados.length} recursos disponibles
              </p>
            </div>
          </div>

          {/* Búsqueda */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o descripción..."
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setPagina(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {(busqueda || filtroAreas.length > 0) && (
              <button
                onClick={limpiarFiltros}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300"
              >
                Limpiar filtros
              </button>
            )}
          </div>

          {/* Filtros por área */}
          {areas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {areas.map((area) => (
                <button
                  key={area}
                  onClick={() => toggleArea(area)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filtroAreas.includes(area)
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {area}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-500">Cargando recursos...</p>
          </div>
        ) : recursosFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {busqueda || filtroAreas.length > 0
                ? "No se encontraron recursos con esos filtros."
                : "No hay recursos en esta categoría."}
            </p>
          </div>
        ) : (
          <>
            {/* Grid de recursos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
              {recursosPaginados.map((recurso, i) => (
                <div
                  key={recurso.id}
                  className="relative bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition overflow-hidden group animate-recurso"
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorito(recurso);
                    }}
                    className="absolute top-3 right-3 text-red-500 hover:scale-110 transition-transform z-20"
                    title="Agregar a favoritos"
                  >
                    {favoritos.some((r) => r.id === recurso.id) ? "❤️" : "🤍"}
                  </button>

                  <img
                    src={imagenes[recurso.id]}
                    alt={recurso.nombre}
                    className="w-full h-40 object-cover"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_RESOURCE_IMAGE;
                    }}
                  />

                  <div className="px-4 pb-4 pt-3">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {recurso.nombre}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {recurso?.categoria?.nombre || "Recurso"}
                    </p>
                  </div>

                  <TutorialLink
                    recurso={recurso}
                    className="absolute bottom-3 right-14 z-20 inline-flex items-center justify-center w-9 h-9 rounded-full border shadow-sm bg-white/90 text-gray-700 hover:bg-white [&_span]:sr-only"
                  />

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRecursoSeleccionado(recurso);
                    }}
                    className="absolute bottom-3 right-3 z-20 inline-flex items-center justify-center w-9 h-9 rounded-full border shadow-sm bg-white/90 text-gray-600 hover:bg-white"
                    title="Información"
                  >
                    <Info className="w-4 h-4" />
                  </button>

                  <a
                    href={buildRecursoGoUrl(API_BASE, recurso.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      registrarRecursoReciente(recurso);
                      logUsoRecurso(API_BASE, recurso.id);
                    }}
                    className="absolute inset-0 z-10"
                  >
                    <span className="sr-only">Abrir {recurso.nombre}</span>
                  </a>
                </div>
              ))}
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
              <div className="flex justify-center items-center gap-2">
                <button
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  disabled={pagina === 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>

                {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(
                  (num) => (
                    <button
                      key={num}
                      onClick={() => setPagina(num)}
                      className={`w-10 h-10 rounded-lg transition-colors ${
                        pagina === num
                          ? "bg-blue-600 text-white"
                          : "border border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      {num}
                    </button>
                  )
                )}

                <button
                  onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                  disabled={pagina === totalPaginas}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {recursoSeleccionado && (
        <InfoModal
          recurso={recursoSeleccionado}
          imagenSrc={imagenes[recursoSeleccionado.id]}
          onClose={() => setRecursoSeleccionado(null)}
        />
      )}
    </div>
  );
}
